<script setup lang="ts">
import { computed } from 'vue';
import {
  useActivities,
  useCrm,
  useStringFunctions,
} from '@apparatix/composables';
import MiniCard from '@apparatix/library/MiniCard.vue';
import { Columns, Column as ColumnSection } from '@apparatix/library/core';
import type { AssociationAccount, AssociationContact } from '@apparatix/types/Leads';
import { AccountAssociated } from '@apparatix/types/Account';
import type { Activity } from '@apparatix/schemas/Activities';
import { useRouter, useRoute } from 'vue-router';
import type { FlyoutNavigationContext } from './types';
import { RelationshipType } from '@apparatix/schemas/Relationship.ts';
import BuildingIcon from '@primeicons/vue/building';
import IdCardIcon from '@primeicons/vue/id-card';
import CalendarIcon from '@primeicons/vue/calendar';

const props = defineProps<{
  showAccountMiniCard: boolean,
  showContactMiniCard: boolean,
  miniCardAccount?: AssociationAccount | AccountAssociated,
  miniCardContact?: AssociationContact,
  accountMiniCardLoading: boolean,
  contactMiniCardLoading: boolean,
  lastTask?: Activity,
  upcomingTask?: Activity,
  isLoading: boolean,
}>();

type Emits = {
  (e: 'post-activity', typeId: number): void;
}

const emit = defineEmits<Emits>();

const { getFormattedMiniCardTitle } = useActivities();
const { getFullName } = useStringFunctions();
const { isDetailTab } = useCrm();

const router = useRouter();
const route = useRoute();

const onNavigate = (context: FlyoutNavigationContext) => {
  const { basePath } = route.meta;

  // The tabId name here is a bit misleading, valid values are
  // 'overview' | 'relationships' | 'deals' | 'activity' | 'files'
  if (context.tabId && !isDetailTab(context.tabId)) {
    return;
  }
  const path = context.tabId
    ? `${basePath}/detail/${context.view.toLowerCase()}/${context.id}/${context.tabId}`
    : `${basePath}/detail/${context.view.toLowerCase()}/${context.id}`;
  router.push({ path });
};

const miniCardContactName = computed(() => props.miniCardContact ? getFullName(props.miniCardContact) : '');

// Account visibility: require show flag and account presence (id or name)
const accountVisible = computed(() => {
  const show = props.showAccountMiniCard;
  const acct = props.miniCardAccount;
  const hasAccount = !!acct && (acct?.c2Id !== null || (acct?.c2Name && acct.c2Name.trim().length > 0));

  return show && hasAccount;
});

// Contact visibility: require show flag + either a contact object (id or non-empty object) OR a non-empty name.
// Also don't show if contactName === accountName (only if contactName exists)
const contactVisible = computed(() => {
  const show = props.showContactMiniCard;
  const contact = props.miniCardContact;
  const contactName = miniCardContactName;
  const accountName = props.miniCardAccount?.c2Name;

  const hasContactObject = !!contact && (contact.id !== null || Object.keys(contact).length > 0);
  const hasContactName = typeof contactName.value === 'string' && contactName.value.trim().length > 0;

  if (!show) return false;
  if (!hasContactObject && !hasContactName) return false;
  return !(hasContactName && accountName && contactName.value === accountName);
});

// Tasks visibility (use unref so it works with refs or raw values)
const lastVisible = computed(() => props.lastTask !== undefined);
const upcomingVisible = computed(() => props.upcomingTask !== undefined);

// Safe formatted titles (won't call formatter when task is null)
const formattedLastTitle = computed(() => props.lastTask ? getFormattedMiniCardTitle(props.lastTask) : '');
const formattedUpcomingTitle = computed(() => props.upcomingTask ? getFormattedMiniCardTitle(props.upcomingTask) : '');

// Total visible and first-row allocation (preserve order: account, contact, last, upcoming)
const totalVisible = computed(() => [accountVisible.value, contactVisible.value, lastVisible.value, upcomingVisible.value].filter(Boolean).length);

const firstRow = computed(() => {
  const row = {
    account: false, contact: false, last: false, upcoming: false,
  };
  let slots = 0;
  if (accountVisible.value && slots < 2) { row.account = true; slots+=1; }
  if (contactVisible.value && slots < 2) { row.contact = true; slots+=1; }
  if (lastVisible.value && slots < 2) { row.last = true; slots+=1; }
  if (upcomingVisible.value&& slots < 2) { row.upcoming= true; }
  return row;
});

const getCurrentView = (): RelationshipType => {
  const segments = route.path.split('/');

  // A little gross forcing a type here
  // @TODO: should have a type check validation
  return <RelationshipType>segments[4];
};

const getActivityContext = (): FlyoutNavigationContext => ({
  id: Number(route.params.id),
  view: getCurrentView(),
  tabId: 'activity',
});

const forwardPostActivity = (payload) => {
  emit('post-activity', payload);
};

const buildModelObject = (id: number, name: string, view: string) => ({ id, name, view });

</script>

<template>
  <!-- single row when <= 2 visibles -->
  <Columns v-if="totalVisible <= 2" class="lg:gap-x-6">
    <ColumnSection v-if="accountVisible" class="lg:max-w-[50%]">
      <MiniCard
        :icon="BuildingIcon"
        icon-color="var(--p-teal-500)"
        :subtitle="miniCardAccount?.c2Type"
        :title="miniCardAccount?.c2Name"
        :account-label="miniCardAccount?.accountLabel"
        :phone="miniCardAccount?.addressPhoneNumbers?.[0]?.number"
        hide-email-button
        :loading="accountMiniCardLoading"
        :model-object="buildModelObject(miniCardAccount?.c2Id, miniCardAccount?.c2Name, 'accounts')"
        @on:title-click="onNavigate({ id: miniCardAccount.c2Id, view: 'account' })"
        @post-activity="forwardPostActivity"
      />
    </ColumnSection>
    <ColumnSection v-if="contactVisible" class="lg:max-w-[50%]">
      <MiniCard
        :icon="IdCardIcon"
        icon-color="var(--p-fuchsia-500)"
        subtitle="Primary Contact"
        :title="miniCardContactName"
        :email="miniCardContact?.addressEmails?.[0]?.email"
        :phone="miniCardContact?.addressPhoneNumbers?.[0]?.number"
        :loading="contactMiniCardLoading"
        :model-object="buildModelObject(miniCardContact?.id, miniCardContactName, 'contacts')"
        @on:title-click="onNavigate({ id: miniCardContact?.id, view: 'contact' })"
        @post-activity="forwardPostActivity"
      />
    </ColumnSection>
    <ColumnSection v-if="lastVisible" class="lg:max-w-[50%]">
      <MiniCard
        :icon="CalendarIcon"
        icon-color="var(--p-blue-500)"
        subtitle="Last Activity"
        :title="formattedLastTitle"
        :loading="props.isLoading"
        :hide-phone-button="true"
        :hide-email-button="true"
        @on:title-click="onNavigate(getActivityContext())"
      />
    </ColumnSection>
    <ColumnSection v-if="upcomingVisible" class="lg:max-w-[50%]">
      <MiniCard
        :icon="CalendarIcon"
        icon-color="var(--p-blue-500)"
        subtitle="Upcoming Activity"
        :title="formattedUpcomingTitle"
        :loading="props.isLoading"
        :hide-phone-button="true"
        :hide-email-button="true"
        @on:title-click="onNavigate(getActivityContext())"
      />
    </ColumnSection>
  </Columns>
  <!-- two rows when > 2 visible (preserve order) -->
  <template v-else>
    <Columns class="lg:gap-x-6">
      <ColumnSection v-if="firstRow.account" class="lg:max-w-[50%]">
        <MiniCard
          :icon="BuildingIcon"
          icon-color="var(--p-teal-500)"
          :subtitle="miniCardAccount?.c2Type"
          :title="miniCardAccount?.c2Name"
          :account-label="miniCardAccount?.accountLabel"
          :phone="miniCardAccount?.addressPhoneNumbers?.[0]?.number"
          hide-email-button
          :loading="accountMiniCardLoading"
          :model-object="buildModelObject(miniCardAccount?.c2Id, miniCardAccount?.c2Name, 'accounts')"
          @on:title-click="onNavigate({ id: miniCardAccount.c2Id, view: 'account' })"
          @post-activity="forwardPostActivity"
        />
      </ColumnSection>
      <ColumnSection v-if="firstRow.contact" class="lg:max-w-[50%]">
        <MiniCard
          :icon="IdCardIcon"
          icon-color="var(--p-fuchsia-500)"
          subtitle="Primary Contact"
          :title="miniCardContactName"
          :email="miniCardContact?.addressEmails?.[0]?.email"
          :phone="miniCardContact?.addressPhoneNumbers?.[0]?.number"
          :loading="contactMiniCardLoading"
          :model-object="buildModelObject(miniCardContact?.id, miniCardContactName, 'contacts')"
          @on:title-click="onNavigate({ id: miniCardContact?.id, view: 'contact' })"
          @post-activity="forwardPostActivity"
        />
      </ColumnSection>
      <ColumnSection v-if="firstRow.last" class="lg:max-w-[50%]">
        <MiniCard
          :icon="CalendarIcon"
          icon-color="var(--p-blue-500)"
          subtitle="Last Activity"
          :title="formattedLastTitle"
          :loading="props.isLoading"
          :hide-phone-button="true"
          :hide-email-button="true"
          @on:title-click="onNavigate(getActivityContext())"
        />
      </ColumnSection>
      <ColumnSection v-if="firstRow.upcoming" class="lg:max-w-[50%]">
        <MiniCard
          :icon="CalendarIcon"
          icon-color="var(--p-blue-500)"
          subtitle="Upcoming Activity"
          :title="formattedUpcomingTitle"
          :loading="props.isLoading"
          :hide-phone-button="true"
          :hide-email-button="true"
          @on:title-click="onNavigate(getActivityContext())"
        />
      </ColumnSection>
    </Columns>
    <Columns class="lg:gap-x-6">
      <ColumnSection v-if="accountVisible && !firstRow.account" class="lg:max-w-[50%]">
        <MiniCard
          :icon="BuildingIcon"
          icon-color="var(--p-teal-500)"
          :subtitle="miniCardAccount?.c2Type"
          :title="miniCardAccount?.c2Name"
          :account-label="miniCardAccount?.accountLabel"
          :phone="miniCardAccount?.addressPhoneNumbers?.[0]?.number"
          hide-email-button
          :loading="accountMiniCardLoading"
          :model-object="buildModelObject(miniCardAccount?.c2Id, miniCardAccount?.c2Name, 'accounts')"
          @on:title-click="onNavigate({ id: miniCardAccount.c2Id, view: 'account' })"
          @post-activity="forwardPostActivity"
        />
      </ColumnSection>
      <ColumnSection v-if="contactVisible && !firstRow.contact" class="lg:max-w-[50%]">
        <MiniCard
          :icon="IdCardIcon"
          icon-color="var(--p-fuchsia-500)"
          subtitle="Primary Contact"
          :title="miniCardContactName"
          :email="miniCardContact?.addressEmails?.[0]?.email"
          :phone="miniCardContact?.addressPhoneNumbers?.[0]?.number"
          :loading="contactMiniCardLoading"
          :model-object="buildModelObject(miniCardContact?.id, miniCardContactName, 'contacts')"
          @on:title-click="onNavigate({ id: miniCardContact?.id, view: 'contact' })"
          @post-activity="forwardPostActivity"
        />
      </ColumnSection>
      <ColumnSection v-if="lastVisible && !firstRow.last" class="lg:max-w-[50%]">
        <MiniCard
          :icon="CalendarIcon"
          icon-color="var(--p-blue-500)"
          subtitle="Last Activity"
          :title="formattedLastTitle"
          :loading="props.isLoading"
          :hide-phone-button="true"
          :hide-email-button="true"
          @on:title-click="onNavigate(getActivityContext())"
        />
      </ColumnSection>
      <ColumnSection v-if="upcomingVisible && !firstRow.upcoming" class="lg:max-w-[50%]">
        <MiniCard
          :icon="CalendarIcon"
          icon-color="var(--p-blue-500)"
          subtitle="Upcoming Activity"
          :title="formattedUpcomingTitle"
          :loading="props.isLoading"
          :hide-phone-button="true"
          :hide-email-button="true"
          @on:title-click="onNavigate(getActivityContext())"
        />
      </ColumnSection>
    </Columns>
  </template>
</template>
